import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { request } from "../../../../config/helpers/axios-instance";
import { isRequestError } from "../../../hooks/request-error";
import PageContainer from "../../../components/PageContainer";
import EmptyState from "../../../components/EmptyState";
import Pagination from "../../../components/Pagination";
import TableSkeleton from "../../../components/TableSkeleton";
import DateRangeFilter from "../../../components/filters/DateRangeFilter";
import ChipSingleSelect from "../../../components/filters/ChipSingleSelect";
import {
    TAT_SORT_OPTIONS,
    DEFAULT_TAT_SORT,
    DEFAULT_TAT_STATUS_GROUP,
    TAT_PARTNER_TYPES,
    DEFAULT_TAT_PARTNER_TYPE,
    TAT_PARTNER_SOURCES,
    PARTNER_TYPES_ACCEPTING_PARTNER_SOURCE,
} from "../../constants/tatFilters";
import SearchInput from "../../../components/filters/SearchInput";
import useTatOrders from "../../hooks/useTatOrders";
import useTatFilters from "../../hooks/useTatFilters";
import TatTable, { buildExportColumns } from "./components/TatTable";
import TatTimelineModal from "./components/TatTimelineModal";
import TableToolbar from "../../../components/TableToolbar";
import { DownLoadTable } from "../../../../utils/DownloadtablesInExcel";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";

// TAT Dashboard — reads live data from pillup-api's GET /api/ops/tat/orders
// and /tat/filters (pillup-api/controller/ops/tat.js) via useTatOrders/
// useTatFilters. Those hooks own the adapter between the real wire contract
// and the shape TatTable expects — see each hook's header comment.
//
// Status chips and sort-by both render from useTatFilters' live
// `statusGroups`/`sortOptions` (Phase 13, pillup-api/docs/tat-dashboard/
// execution-plan.md) rather than a hardcoded local list — see
// src/v2/ops/constants/tatFilters.js's header comment for why that matters: this
// file's own hardcoded status groups had already drifted from the server's
// real ones by the time Phase 13 shipped.
//
// __tests__/tat-dashboard.test.jsx still exercises the component primitives
// (TatCell/TatTable) directly against the frozen fixture in
// ./__fixtures__/sample-response.json — that fixture is test data for those
// components' own contract, not a stand-in for the network layer.
//
// isLoading/isError below are real branches, not a defensive afterthought:
// most stage timestamps genuinely don't exist yet for most orders (see
// pillup-api/docs/tat-dashboard/explanation-why-tat-is-hard.md), and
// axios-instance.js's `request()` hard-redirects to `/404` on any non-401
// HTTP failure — a transient backend error takes the whole app down, not
// just this page. That's a shared, pre-existing behavior of `request()`,
// not something specific to this page's queries.

/** URL param names, kept in one place so read and write can't drift. */
const PARAM = {
    sort: "sort",
    group: "group",
    q: "q",
    from: "from",
    to: "to",
    page: "page",
    partnerType: "partnerType",
    partnerSource: "partnerSource",
};

function TatDashboard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const sort = searchParams.get(PARAM.sort) || DEFAULT_TAT_SORT;
    const statusGroup = searchParams.get(PARAM.group) || DEFAULT_TAT_STATUS_GROUP;

    const q = searchParams.get(PARAM.q) || "";
    const from = searchParams.get(PARAM.from) || "";
    const to = searchParams.get(PARAM.to) || "";
    const page = Math.max(Number(searchParams.get(PARAM.page)) || 1, 1);

    // Mirrors the server's own default rather than "no filter": omit `partnerType` and
    // the backend applies DIRECT anyway, so the chips would otherwise show nothing
    // selected while a selection was quietly in force.
    //
    // Validated against the chips rather than taken on trust. The server still accepts
    // ALL, which this page no longer offers (see TAT_PARTNER_TYPES) — left unchecked, a
    // hand-written `?partnerType=ALL` would load a mixed page with no chip selected to
    // explain it. Anything unrecognised falls back to the default.
    const requestedPartnerType = searchParams.get(PARAM.partnerType);
    const partnerType = TAT_PARTNER_TYPES.some((option) => option.id === requestedPartnerType)
        ? requestedPartnerType
        : DEFAULT_TAT_PARTNER_TYPE;
    const partnerScopeAllowsSource = PARTNER_TYPES_ACCEPTING_PARTNER_SOURCE.includes(partnerType);
    // Read as "" under DIRECT, not merely hidden: a stale partnerSource left in the URL
    // would otherwise keep narrowing a query whose control is off screen. Validated
    // against the known sources for the same reason partnerType is.
    const requestedPartnerSource = searchParams.get(PARAM.partnerSource);
    const partnerSource =
        partnerScopeAllowsSource &&
        TAT_PARTNER_SOURCES.some((option) => option.id === requestedPartnerSource)
            ? requestedPartnerSource
            : "";

    // `statusGroup` is the server's own key (e.g. "TO_PROCESS"), read straight from
    // the URL — unlike the old label-expansion scheme this replaced, there is no
    // client-side lookup standing between the URL and the wire, so ordersQuery below
    // needs no "wait for filters to resolve first" gate: the value is already what
    // the server expects, whether or not the chip labels have loaded yet.
    const ordersQuery = useTatOrders({
        q,
        from,
        to,
        statusGroup,
        partnerType,
        partnerSource,
        page,
        size: DEFAULT_PAGE_SIZE,
        sort,
    });
    const orders = ordersQuery.data?.orders ?? [];
    const intervals = ordersQuery.data?.meta?.intervals ?? [];
    const unavailableStages = ordersQuery.data?.meta?.unavailableStages ?? [];
    const pagination = ordersQuery.data?.pagination ?? { page, size: DEFAULT_PAGE_SIZE, total: 0 };

    // Status chips: the "All" chip is this page's own convention (see
    // DEFAULT_TAT_STATUS_GROUP's comment), prepended in front of whatever
    // /ops/tat/filters actually returns — nothing here is a local copy of which
    // codes belong to which chip.
    const filtersQuery = useTatFilters();
    const statusChipOptions = [
        { id: DEFAULT_TAT_STATUS_GROUP, label: "All" },
        ...(filtersQuery.data?.statusGroups ?? []).map((group) => ({ id: group.key, label: group.label })),
    ];

    // Selection exists here only to drive the export — TAT has no bulk action and no
    // write path at all. Keyed by order id rather than row index so a page change or a
    // refetch can't silently move the selection onto different orders.
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    // The order whose timeline is open, or null. The clicked row object is held
    // directly rather than its id-plus-a-lookup: a transition log is immutable history,
    // so there is nothing to re-resolve, and holding the row means a background refetch
    // or a filter change cannot yank the dialog closed while it is being read.
    const [timelineRow, setTimelineRow] = useState(null);

    // Opening the order this measurement is about. Like the Orders and B2B row actions,
    // this deliberately leaves the v2 shell for the legacy editor at /order/edit/:id —
    // the same accepted seam the console's other deep links use.
    const handleEditOrder = (row) => navigate(`/order/edit/${row.id}`);

    /**
     * Legacy's delete rule, evaluated at click time.
     *
     * /ops/tat/orders does not return `payments`, so this fetches the one order the user
     * is about to delete (~1s) rather than paying for a lookup per visible row. Resolves
     * a reason string to block, or null to allow — the shape RowActions expects.
     */
    const checkDeletable = async (row) => {
        const result = await request({ url: `/userOrder/admin/orderDetails/${row.id}`, method: "GET" });
        if (isRequestError(result) || !result?.data?.success) {
            return "Couldn't load this order's payments, so it wasn't deleted.";
        }
        const payments = result.data.data?.payments || [];
        const live = payments.filter((p) => p.status === "SUCCESS" || p.status === "PENDING");
        return live.length
            ? `Order ${row.id} has a ${live[0].status.toLowerCase()} payment and can't be deleted.`
            : null;
    };

    const handleDeleteOrder = async (row) => {
        const result = await request({ url: `/userOrder/admin/id/${row.id}`, method: "DELETE" });
        if (isRequestError(result) || !result?.data?.success) {
            // eslint-disable-next-line no-alert
            window.alert(`Order ${row.id} could not be deleted.`);
            return;
        }
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(row.id);
            return next;
        });
        ordersQuery.refetch();
    };

    const toggleRow = (id) =>
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const allSelected = orders.length > 0 && orders.every((row) => selectedIds.has(row.id));
    const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(orders.map((row) => row.id)));

    /**
     * Export the rows currently on screen, or just the ticked ones if any are ticked.
     *
     * Columns are rebuilt here from the same `intervals` the table renders, so a stage
     * the backend starts instrumenting appears in the spreadsheet without a code change,
     * exactly as it does in the table. Interval cells go through TatTable's
     * `exportInterval`, which keeps a stalled or swept figure labelled as such — an
     * export that flattened those into bare durations would let an analyst average a
     * stalled order in as though it had completed.
     */
    const handleDownloadExcel = () => {
        const rowsToExport = selectedIds.size ? orders.filter((row) => selectedIds.has(row.id)) : orders;
        if (!rowsToExport.length) return;
        const columns = buildExportColumns(intervals, rowsToExport);
        DownLoadTable(
            "TAT-Dashboard",
            "TAT",
            columns.map((column) => column.label),
            rowsToExport.map((row) => columns.map((column) => column.value(row)))
        );
    };

    /**
     * Write filter/page state back into the URL. Any filter edit resets to
     * page 1 — staying on page 3 of a result set that just shrank to one page
     * is the classic way to show a user an empty table and no explanation.
     */
    const updateParams = (changes, { resetPage = true } = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => {
            const serialized = Array.isArray(value) ? value.join(",") : value;
            if (serialized) next.set(key, serialized);
            else next.delete(key);
        });
        if (resetPage) next.delete(PARAM.page);
        setSearchParams(next, { replace: true });
    };

    /**
     * Changing the partner scope drops any source that no longer applies.
     *
     * Switching to DIRECT while a source is picked would otherwise leave
     * `partnerSource` in the URL describing the empty set — direct orders that came
     * through a partner — and the control to clear it would be hidden by the same
     * switch.
     */
    const handlePartnerTypeChange = (next) => {
        updateParams({
            [PARAM.partnerType]: next === DEFAULT_TAT_PARTNER_TYPE ? "" : next,
            [PARAM.partnerSource]: PARTNER_TYPES_ACCEPTING_PARTNER_SOURCE.includes(next)
                ? partnerSource
                : "",
        });
    };

    const hasActiveFilters = Boolean(
        q ||
            from ||
            to ||
            statusGroup !== DEFAULT_TAT_STATUS_GROUP ||
            partnerType !== DEFAULT_TAT_PARTNER_TYPE ||
            partnerSource
    );

    return (
        <PageContainer>
            <header>
                <h1 className="text-2xl font-bold text-black font-sans">TAT Dashboard</h1>
                <p className="mt-1 max-w-[520px] text-sm font-sans text-gray-500">
                    Stage-by-stage turnaround for every order. Cells state plainly what is measured,
                    what is still moving, what is stuck, and what was never tracked. Select an order
                    id to read the status transitions behind its numbers.
                </p>
            </header>

            {/* Legacy has no filter card — the filters sit bare on the page
                background (mirrors orders.jsx:445/:507), and the search input
                carries its own label in the placeholder. */}
            <section
                aria-label="Filters"
                className="flex flex-wrap gap-4 items-center relative my-2"
            >
                <div className="min-w-[220px] flex-1">
                    <SearchInput
                        value={q}
                        onChange={(value) => updateParams({ [PARAM.q]: value })}
                        // Verified live against the real endpoint: search matches order id,
                        // inventoryId and pincode. It does NOT match patient name or status
                        // text (both tested, both 0 results) — this placeholder used to
                        // promise those two and silently never deliver.
                        placeholder={pagination.total ? `${pagination.total} records...` : "Search order ID, inventory, pincode"}
                    />
                </div>
                <DateRangeFilter
                    from={from}
                    to={to}
                    idPrefix="tat-order-date"
                    fromLabel="Order date from"
                    toLabel="Order date to"
                    onChange={(range) =>
                        updateParams({ [PARAM.from]: range.from, [PARAM.to]: range.to })
                    }
                />
                <div className="flex items-center gap-2">
                    <span className="text-sm font-bold font-sans text-black">Units: hours</span>
                </div>
            </section>

            {/* Sort by — single-select, mirroring the prototype. The ids are the
                backend's own SORT_SQL keys, so a chip maps straight to `?sort=`. */}
            <section className="my-2">
                <ChipSingleSelect
                    inline
                    label="Sort by"
                    value={sort}
                    onChange={(next) => updateParams({ [PARAM.sort]: next })}
                    options={TAT_SORT_OPTIONS.map((option) => ({
                        id: option.id,
                        label: option.label,
                        disabled: Boolean(option.broken),
                        title: option.broken,
                    }))}
                />
            </section>

            {/* Partner scope. The stages themselves are NOT switched here: a partner
                order's journey starts at Order Confirmed rather than at a lead, and the
                interval list that describes that comes from the server's own
                `meta.intervals` for the query — so the table's columns and the stage
                drill-down follow the scope automatically, with no stage list in this
                file to keep in step. */}
            <section className="my-2 flex flex-wrap items-center gap-4">
                <ChipSingleSelect
                    inline
                    label="Orders"
                    value={partnerType}
                    onChange={handlePartnerTypeChange}
                    options={TAT_PARTNER_TYPES}
                />

                {partnerScopeAllowsSource && (
                    // Deliberately NOT given a visible <label>. Two bold labels on one
                    // row ("Orders", "Partner") read as two separate filters when this is
                    // a refinement of the chip beside it — and a bare <label> here picks
                    // up a green from Flowbite's injected base styles, so the subordinate
                    // control was rendering louder than the heading it sat under. The
                    // accessible name is carried by `aria-label`; the first option names
                    // the control on screen.
                    //
                    // Sized and coloured as a chip (Chip.jsx's own geometry: 10px,
                    // px-2 py-1, rounded-[10px], borderless) so it reads as part of the
                    // same control group rather than a form field parked next to one.
                    // `border-0` is explicit because @tailwindcss/forms puts a 1px border
                    // on every select in the base layer; `w-auto max-w-[200px]` likewise,
                    // because the same plugin stretches an unconstrained select to fill
                    // its flex line.
                    <select
                        id="tat-partner-source"
                        aria-label="Partner source"
                        value={partnerSource}
                        onChange={(event) =>
                            updateParams({ [PARAM.partnerSource]: event.target.value })
                        }
                        className={`w-auto max-w-[200px] truncate rounded-[10px] border-0 py-1 pl-2 text-[10px] font-sans ${
                            // A filter that is doing something looks engaged, in the same
                            // green the active chip uses.
                            partnerSource ? "bg-elm-500 text-white-50" : "bg-gray-200 text-[#333]"
                        }`}
                    >
                        {/* Named for what it does, not left blank: under PARTNER this
                            means every partner source, which is not the same as no
                            filter. */}
                        <option value="">All partners</option>
                        {TAT_PARTNER_SOURCES.map((source) => (
                            <option key={source.id} value={source.id}>
                                {source.label}
                            </option>
                        ))}
                    </select>
                )}
            </section>

            {/* Status — single-select, server-driven (see useTatFilters' header
                comment): the group ids/labels come straight from
                /ops/tat/filters, "All" is this page's own prepended default. */}
            <section className="my-2">
                <ChipSingleSelect
                    inline
                    label="Status"
                    value={statusGroup}
                    onChange={(next) => updateParams({ [PARAM.group]: next })}
                    options={statusChipOptions}
                />
            </section>

            {ordersQuery.isLoading || ordersQuery.isIdle ? (
                // isIdle covers the deferred-while-filters-resolve window
                // above (react-query v3: a disabled query with no data yet is
                // "idle", not "loading") — without it, the brief moment
                // before useTatFilters resolves would fall through to the
                // "no orders match" empty state instead of a loading skeleton.
                <div
                    data-testid="tat-orders-loading"
                    className="block w-full overflow-x-auto py-4 bg-[white] rounded-2xl shadow-soft-xl mt-4"
                >
                    <table className="table-auto w-full font-sans">
                        <tbody>
                            {/* `intervals` comes from ordersQuery.data, which is
                                always undefined while loading/idle — cols is
                                therefore always the 8 fallback below, not a
                                real column count. Fixed at 8 rather than
                                writing dead conditional logic for it. */}
                            <TableSkeleton rows={DEFAULT_PAGE_SIZE} cols={8} />
                        </tbody>
                    </table>
                </div>
            ) : ordersQuery.isError ? (
                <div
                    data-testid="tat-orders-error"
                    className="block py-4 bg-[white] rounded-2xl shadow-soft-xl mt-4"
                >
                    <EmptyState
                        title="Couldn't load orders"
                        hint="Something went wrong reaching the TAT service. Try again in a moment."
                    />
                </div>
            ) : orders.length === 0 ? (
                <div className="block py-4 bg-[white] rounded-2xl shadow-soft-xl mt-4">
                    <EmptyState
                        title="No orders match these filters"
                        hint={
                            hasActiveFilters
                                ? "Try widening the date range or clearing a chip."
                                : "There is nothing in this dataset yet."
                        }
                    />
                </div>
            ) : (
                // No flex/gap here: the table card carries its own `mt-4` and
                // the legacy `.btn_div` pager carries `margin-top: 15px`.
                <div>
                    <TableToolbar
                        allSelected={allSelected}
                        onToggleAll={toggleAll}
                        onDownloadExcel={handleDownloadExcel}
                    />
                    <TatTable
                        intervals={intervals}
                        rows={orders}
                        selectedIds={selectedIds}
                        onToggleRow={toggleRow}
                        onToggleAll={toggleAll}
                        onEditOrder={handleEditOrder}
                        onDeleteOrder={handleDeleteOrder}
                        onCheckDeletable={checkDeletable}
                        onViewTimeline={setTimelineRow}
                    />
                    <Pagination
                        page={pagination.page}
                        size={pagination.size}
                        total={pagination.total}
                        onChange={(nextPage) =>
                            updateParams({ [PARAM.page]: String(nextPage) }, { resetPage: false })
                        }
                    />
                </div>
            )}

            {timelineRow && (
                <TatTimelineModal
                    row={timelineRow}
                    // The same `meta.intervals` the table's columns come from, so the
                    // drill-down and the row it opened from can never disagree about
                    // which stages exist.
                    intervals={intervals}
                    unavailableStages={unavailableStages}
                    onClose={() => setTimelineRow(null)}
                />
            )}
        </PageContainer>
    );
}

export default TatDashboard;
